import { CalendarDays, CheckCircle2, Hash, Link2, MessageCircle, UserRound, Users } from 'lucide-react';
import { useState } from 'react';
import {
  apiMarkAllNotificationsRead,
  apiMarkNotificationRead,
} from '../api/client';

const notificationTypeKeys = {
  'Task Assigned': 'notificationTaskAssigned',
  'Comment Mentioned': 'notificationCommentMentioned',
  'Task Status Changed': 'notificationTaskStatusChanged',
  'Due Date Approaching': 'notificationDueDateApproaching',
  'New Project Member': 'notificationNewProjectMember',
  'Meeting Link Shared': 'notificationMeetingLinkShared',
  'New Post in Channel': 'notificationNewPostInChannel',
};

const notificationGroupKeys = {
  Today: 'today',
  Yesterday: 'yesterday',
  'This Week': 'thisWeek',
};

function NotificationsCenter({
  notifications,
  onRefreshNotifications,
  onNavigate,
  compact = false,
  onClose,
  dir,
  t,
  onToast,
}) {
  const [filter, setFilter] = useState('All');
  const [busyIds, setBusyIds] = useState(() => new Set());
  const typeMeta = {
    'Task Assigned': { color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30', icon: UserRound },
    'Comment Mentioned': { color: 'text-brand-500', bg: 'bg-brand-100 dark:bg-brand-900/30', icon: MessageCircle },
    'Task Status Changed': { color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: CheckCircle2 },
    'Due Date Approaching': { color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30', icon: CalendarDays },
    'New Project Member': { color: 'text-teal-500', bg: 'bg-teal-100 dark:bg-teal-900/30', icon: Users },
    'Meeting Link Shared': { color: 'text-brand-600', bg: 'bg-brand-100 dark:bg-brand-900/30', icon: Link2 },
    'New Post in Channel': { color: 'text-pink-500', bg: 'bg-pink-100 dark:bg-pink-900/30', icon: Hash },
  };

  const filtered = notifications.filter((item) => {
    if (filter === 'Unread') return item.unread;
    if (filter === 'Mentions') return item.type === 'Comment Mentioned';
    if (filter === 'Assignments') return item.type === 'Task Assigned';
    return true;
  });

  const grouped = ['Today', 'Yesterday', 'This Week'].reduce((acc, group) => {
    acc[group] = filtered.filter((item) => item.group === group);
    return acc;
  }, {});

  const markAllRead = async () => {
    try {
      await apiMarkAllNotificationsRead();
      await onRefreshNotifications();
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : String(err));
    }
  };

  const openItem = async (item) => {
    if (busyIds.has(item.id)) return;
    setBusyIds((prev) => new Set(prev).add(item.id));
    try {
      if (item.unread) {
        await apiMarkNotificationRead(item.id);
        await onRefreshNotifications();
      }
      onNavigate(item.linkTarget);
      if (compact && onClose) onClose();
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const body = (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('notificationsTitle')}</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={markAllRead}
            className="rounded-md border border-brand-200 px-2.5 py-1 text-xs dark:border-brand-900"
          >
            {t('markAllAsRead')}
          </button>
          {compact && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-brand-200 px-2.5 py-1 text-xs dark:border-brand-900"
            >
              {t('close')}
            </button>
          )}
        </div>
      </div>

      <div className="mb-3">
        <select
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          className="rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-950"
        >
          <option value="All">{t('filterAll')}</option>
          <option value="Unread">{t('filterUnread')}</option>
          <option value="Mentions">{t('filterMentions')}</option>
          <option value="Assignments">{t('filterAssignments')}</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-brand-300 p-6 text-center text-sm text-slate-500 dark:border-brand-900 dark:text-slate-300">
          <div className="text-2xl">🔔</div>
          <p className="mt-2">{t('noNotificationsNow')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped)
            .filter(([, entries]) => entries.length > 0)
            .map(([group, entries]) => (
              <div key={group}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {t(notificationGroupKeys[group] || group)}
                </p>
                <div className="space-y-1.5">
                  {entries.map((item) => {
                    const meta = typeMeta[item.type] || typeMeta['Task Assigned'];
                    const Icon = meta.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        disabled={busyIds.has(item.id)}
                        onClick={() => openItem(item)}
                        className="flex w-full items-start gap-2 rounded-lg border border-brand-200 bg-white p-2 text-left hover:bg-brand-50 disabled:opacity-60 dark:border-brand-900 dark:bg-slate-900 dark:hover:bg-brand-900/20"
                      >
                        <span className={`mt-0.5 rounded-md p-1 ${meta.bg} ${meta.color}`}>
                          <Icon size={12} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-slate-700 dark:text-slate-200">
                            {t(notificationTypeKeys[item.type] || item.type)}
                          </p>
                          <p className="truncate text-xs text-slate-500 dark:text-slate-300">{item.text}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {item.unread && <span className="h-2 w-2 rounded-full bg-brand-500" />}
                          <span className="text-[10px] text-slate-400">{item.timestamp}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      )}
    </>
  );

  if (compact) {
    return (
      <div
        className={`fixed top-20 z-[90] w-full max-w-md rounded-2xl border border-brand-200 bg-white p-3 shadow-2xl dark:border-brand-900 dark:bg-slate-900 ${dir === 'rtl' ? 'left-4' : 'right-4'}`}
      >
        {body}
      </div>
    );
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      {body}
    </section>
  );
}

export default NotificationsCenter;
